import React from 'react';
import {
  Container,
  Typography,
  Card,
  CardContent,
  Grid,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Button,
  Paper
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Public as PublicIcon,
  Assessment as AssessmentIcon,
  Security as SecurityIcon,
  CloudUpload as CloudUploadIcon,
  School as SchoolIcon,
  Handshake as HandshakeIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';

const ATSGlobalSystemDetail: React.FC = () => {
  const navigate = useNavigate();

  const handleBack = () => {
    navigate('/admin/ats-system');
  };

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={handleBack}
          sx={{ mr: 2 }}
          aria-label="Back to ATS System"
        >
          Back to ATS
        </Button>
        <Box>
          <Typography variant="h4" sx={{ color: 'primary.main', fontWeight: 'bold' }}>
            ATS-Global (ATS-G)
          </Typography>
          <Typography variant="subtitle1" color="text.secondary">
            International Technology Transfer and Capacity Building Platform
          </Typography>
        </Box>
      </Box>

      {/* System Overview */}
      <Card sx={{ mb: 3, bgcolor: '#f0f4f8' }}>
        <CardContent>
          <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold' }}>
            Subsystem Overview
          </Typography>
          <Typography variant="body1" paragraph>
            ATS-Global (ATS-G) is the technology transfer and capacity building subsystem that exports U.S. targeting 
            technology, methodologies, and best practices to international partner customs agencies. Through training 
            programs, technical assistance, and shared security vetting platforms, ATS-G strengthens global supply chain 
            security by enabling partner nations to implement risk-based targeting systems aligned with U.S. standards, 
            creating a multilayered international defense against transnational threats.
          </Typography>
        </CardContent>
      </Card>

      {/* 5Ws Summary */}
      <Typography variant="h5" gutterBottom sx={{ color: 'primary.main', fontWeight: 'bold', mt: 4, mb: 3 }}>
        <AssessmentIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
        Subsystem Overview: The 5Ws
      </Typography>

      <Card sx={{ mb: 4 }}>
        <TableContainer>
          <Table aria-label="ATS-Global 5Ws Overview">
            <TableBody>
              <TableRow sx={{ '&:hover': { bgcolor: '#f5f5f5' } }}>
                <TableCell 
                  component="th" 
                  scope="row"
                  sx={{ width: '15%', bgcolor: '#D32F2F', color: 'white', fontWeight: 'bold', fontSize: '1.1rem' }}
                  aria-label="What is ATS-Global"
                >
                  What
                </TableCell>
                <TableCell>
                  <Typography variant="body2">
                    A <strong>technology transfer and capacity building program</strong> that exports <strong>U.S. targeting 
                    technology, analytical methodologies, and risk assessment frameworks</strong> to international partner 
                    customs agencies. Provides <strong>training, technical assistance, software platforms, and operational 
                    support</strong> to help partner nations develop <strong>risk-based targeting capabilities</strong> for 
                    cargo and traveler screening.
                  </Typography>
                </TableCell>
              </TableRow>
              <TableRow sx={{ '&:hover': { bgcolor: '#f5f5f5' } }}>
                <TableCell 
                  component="th" 
                  scope="row"
                  sx={{ bgcolor: '#1976D2', color: 'white', fontWeight: 'bold', fontSize: '1.1rem' }}
                  aria-label="Who uses ATS-Global"
                >
                  Who
                </TableCell>
                <TableCell>
                  <Typography variant="body2">
                    Managed by <strong>CBP Office of International Affairs</strong> and <strong>National Targeting Center 
                    (NTC)</strong>. Serves <strong>foreign customs agencies</strong> in partner countries, <strong>international 
                    organizations</strong> (World Customs Organization, INTERPOL), and <strong>regional security bodies</strong>. 
                    Supported by <strong>CBP trainers, technical advisors, and international liaison officers</strong>.
                  </Typography>
                </TableCell>
              </TableRow>
              <TableRow sx={{ '&:hover': { bgcolor: '#f5f5f5' } }}>
                <TableCell 
                  component="th" 
                  scope="row"
                  sx={{ bgcolor: '#2E7D32', color: 'white', fontWeight: 'bold', fontSize: '1.1rem' }}
                  aria-label="When was ATS-Global deployed"
                >
                  When
                </TableCell>
                <TableCell>
                  <Typography variant="body2">
                    Initiated in <strong>2006-2008</strong> as part of international capacity building efforts. Expanded 
                    through <strong>2010-2015</strong> with technology transfer agreements. Major growth in <strong>2016-2020</strong> 
                    with cloud-based platforms and <strong>2021-present</strong> with AI-enhanced targeting tools. Provides 
                    <strong> ongoing training, technical support, and system updates</strong> to partner nations.
                  </Typography>
                </TableCell>
              </TableRow>
              <TableRow sx={{ '&:hover': { bgcolor: '#f5f5f5' } }}>
                <TableCell 
                  component="th" 
                  scope="row"
                  sx={{ bgcolor: '#F57C00', color: 'white', fontWeight: 'bold', fontSize: '1.1rem' }}
                  aria-label="Where is ATS-Global deployed"
                >
                  Where
                </TableCell>
                <TableCell>
                  <Typography variant="body2">
                    <strong>Program management</strong> at CBP headquarters and National Targeting Center. <strong>Technology 
                    deployed in 40+ countries</strong> across Europe, Asia, Middle East, Africa, and Americas. <strong>Training 
                    centers</strong> at CBP facilities and partner nation locations. <strong>Cloud-based platforms</strong> 
                    accessible globally with secure authentication.
                  </Typography>
                </TableCell>
              </TableRow>
              <TableRow sx={{ '&:hover': { bgcolor: '#f5f5f5' } }}>
                <TableCell 
                  component="th" 
                  scope="row"
                  sx={{ bgcolor: '#7B1FA2', color: 'white', fontWeight: 'bold', fontSize: '1.1rem' }}
                  aria-label="Why ATS-Global exists"
                >
                  Why
                </TableCell>
                <TableCell>
                  <Typography variant="body2">
                    To <strong>strengthen global supply chain security</strong> by building partner nation capacity for 
                    risk-based targeting. Enables <strong>multilayered defense against transnational threats</strong> by 
                    ensuring partner customs can identify high-risk cargo and travelers at origin and transit points. Supports 
                    <strong> U.S. national security interests</strong> through international cooperation and <strong>shared 
                    security vetting standards</strong> aligned with U.S. methodologies.
                  </Typography>
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
      </Card>

      {/* Core Capabilities */}
      <Typography variant="h5" gutterBottom sx={{ color: 'primary.main', fontWeight: 'bold', mt: 4, mb: 3 }}>
        <SecurityIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
        Technology Transfer & Capacity Building
      </Typography>

      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} md={6}>
          <Card sx={{ height: '100%', border: '2px solid #7B1FA2' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <CloudUploadIcon sx={{ fontSize: 40, color: '#7B1FA2', mr: 2 }} />
                <Typography variant="h6" sx={{ color: '#7B1FA2', fontWeight: 'bold' }}>
                  Technology Platforms
                </Typography>
              </Box>
              <Typography variant="body2" paragraph>
                Export of targeting software and analytical tools to partner nations.
              </Typography>
              <Box component="ul" sx={{ pl: 2, '& li': { mb: 1 } }}>
                <li><Typography variant="body2"><strong>Targeting Software:</strong> Adapted ATS modules for partner use</Typography></li>
                <li><Typography variant="body2"><strong>Risk Scoring Engines:</strong> Algorithmic risk assessment tools</Typography></li>
                <li><Typography variant="body2"><strong>Data Analytics:</strong> Pattern recognition and anomaly detection</Typography></li>
                <li><Typography variant="body2"><strong>Cloud Platforms:</strong> Secure hosted solutions for partner access</Typography></li>
                <li><Typography variant="body2"><strong>Integration Tools:</strong> APIs for connecting local systems</Typography></li>
                <li><Typography variant="body2"><strong>Mobile Applications:</strong> Field officer targeting tools</Typography></li>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card sx={{ height: '100%', border: '2px solid #1976D2' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <SchoolIcon sx={{ fontSize: 40, color: '#1976D2', mr: 2 }} />
                <Typography variant="h6" sx={{ color: '#1976D2', fontWeight: 'bold' }}>
                  Training & Education
                </Typography>
              </Box>
              <Typography variant="body2" paragraph>
                Comprehensive training programs for partner customs personnel.
              </Typography>
              <Box component="ul" sx={{ pl: 2, '& li': { mb: 1 } }}>
                <li><Typography variant="body2"><strong>Targeting Methodology:</strong> Risk-based targeting principles</Typography></li>
                <li><Typography variant="body2"><strong>Analytical Techniques:</strong> Data analysis and pattern recognition</Typography></li>
                <li><Typography variant="body2"><strong>System Operations:</strong> Software platform training</Typography></li>
                <li><Typography variant="body2"><strong>Intelligence Analysis:</strong> Threat assessment and evaluation</Typography></li>
                <li><Typography variant="body2"><strong>Best Practices:</strong> U.S. targeting standards and procedures</Typography></li>
                <li><Typography variant="body2"><strong>Train-the-Trainer:</strong> Develop local training capacity</Typography></li>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card sx={{ height: '100%', border: '2px solid #2E7D32' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <HandshakeIcon sx={{ fontSize: 40, color: '#2E7D32', mr: 2 }} />
                <Typography variant="h6" sx={{ color: '#2E7D32', fontWeight: 'bold' }}>
                  Technical Assistance
                </Typography>
              </Box>
              <Typography variant="body2" paragraph>
                Ongoing operational and technical support for partner agencies.
              </Typography>
              <Box component="ul" sx={{ pl: 2, '& li': { mb: 1 } }}>
                <li><Typography variant="body2"><strong>System Implementation:</strong> Deployment and configuration support</Typography></li>
                <li><Typography variant="body2"><strong>Operational Guidance:</strong> Targeting strategy development</Typography></li>
                <li><Typography variant="body2"><strong>Performance Monitoring:</strong> Effectiveness measurement and optimization</Typography></li>
                <li><Typography variant="body2"><strong>Troubleshooting:</strong> Technical issue resolution</Typography></li>
                <li><Typography variant="body2"><strong>System Upgrades:</strong> Software updates and enhancements</Typography></li>
                <li><Typography variant="body2"><strong>Advisory Services:</strong> Strategic planning and policy development</Typography></li>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card sx={{ height: '100%', border: '2px solid #F57C00' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <SecurityIcon sx={{ fontSize: 40, color: '#F57C00', mr: 2 }} />
                <Typography variant="h6" sx={{ color: '#F57C00', fontWeight: 'bold' }}>
                  Standards & Frameworks
                </Typography>
              </Box>
              <Typography variant="body2" paragraph>
                Development of international targeting standards and protocols.
              </Typography>
              <Box component="ul" sx={{ pl: 2, '& li': { mb: 1 } }}>
                <li><Typography variant="body2"><strong>Risk Assessment Standards:</strong> Common evaluation criteria</Typography></li>
                <li><Typography variant="body2"><strong>Data Exchange Protocols:</strong> Secure information sharing formats</Typography></li>
                <li><Typography variant="body2"><strong>Performance Metrics:</strong> Effectiveness measurement standards</Typography></li>
                <li><Typography variant="body2"><strong>Quality Assurance:</strong> Targeting accuracy benchmarks</Typography></li>
                <li><Typography variant="body2"><strong>Privacy Protection:</strong> Data handling and security standards</Typography></li>
                <li><Typography variant="body2"><strong>Interoperability:</strong> System integration requirements</Typography></li>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Partner Nations */}
      <Typography variant="h5" gutterBottom sx={{ color: 'primary.main', fontWeight: 'bold', mt: 4, mb: 3 }}>
        <PublicIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
        Partner Nation Deployment
      </Typography>

      <TableContainer component={Paper} sx={{ mb: 4 }}>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ bgcolor: 'primary.main' }}>
              <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Region</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Partner Countries</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Deployment Status</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Focus Areas</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            <TableRow>
              <TableCell><strong>Europe</strong></TableCell>
              <TableCell>UK, France, Germany, Netherlands, Spain</TableCell>
              <TableCell>Fully Operational</TableCell>
              <TableCell>Cargo targeting, passenger screening</TableCell>
            </TableRow>
            <TableRow sx={{ bgcolor: '#f5f5f5' }}>
              <TableCell><strong>Asia-Pacific</strong></TableCell>
              <TableCell>Japan, South Korea, Singapore, Australia</TableCell>
              <TableCell>Fully Operational</TableCell>
              <TableCell>Maritime security, trade facilitation</TableCell>
            </TableRow>
            <TableRow>
              <TableCell><strong>Middle East</strong></TableCell>
              <TableCell>UAE, Saudi Arabia, Jordan, Israel</TableCell>
              <TableCell>Implementation Phase</TableCell>
              <TableCell>Counterterrorism, WMD prevention</TableCell>
            </TableRow>
            <TableRow sx={{ bgcolor: '#f5f5f5' }}>
              <TableCell><strong>Americas</strong></TableCell>
              <TableCell>Canada, Mexico, Colombia, Brazil, Chile</TableCell>
              <TableCell>Fully Operational</TableCell>
              <TableCell>Drug interdiction, border security</TableCell>
            </TableRow>
            <TableRow>
              <TableCell><strong>Africa</strong></TableCell>
              <TableCell>South Africa, Kenya, Nigeria, Egypt</TableCell>
              <TableCell>Pilot Programs</TableCell>
              <TableCell>Capacity building, infrastructure</TableCell>
            </TableRow>
            <TableRow sx={{ bgcolor: '#f5f5f5' }}>
              <TableCell><strong>Caribbean</strong></TableCell>
              <TableCell>Jamaica, Dominican Republic, Bahamas</TableCell>
              <TableCell>Training Phase</TableCell>
              <TableCell>Maritime interdiction, tourism security</TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>

      {/* Program Scale */}
      <Typography variant="h5" gutterBottom sx={{ color: 'primary.main', fontWeight: 'bold', mt: 4, mb: 3 }}>
        <PublicIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
        Program Scale & Impact
      </Typography>

      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} md={3}>
          <Card sx={{ textAlign: 'center', border: '2px solid #7B1FA2' }}>
            <CardContent>
              <Typography variant="h3" sx={{ color: '#7B1FA2', fontWeight: 'bold' }}>
                40+
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Partner Countries
              </Typography>
              <Typography variant="caption" display="block" sx={{ mt: 1 }}>
                Technology deployed
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={3}>
          <Card sx={{ textAlign: 'center', border: '2px solid #1976D2' }}>
            <CardContent>
              <Typography variant="h3" sx={{ color: '#1976D2', fontWeight: 'bold' }}>
                2,000+
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Officers Trained
              </Typography>
              <Typography variant="caption" display="block" sx={{ mt: 1 }}>
                Annual training volume
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={3}>
          <Card sx={{ textAlign: 'center', border: '2px solid #2E7D32' }}>
            <CardContent>
              <Typography variant="h3" sx={{ color: '#2E7D32', fontWeight: 'bold' }}>
                15+
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Years Operating
              </Typography>
              <Typography variant="caption" display="block" sx={{ mt: 1 }}>
                Program history
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={3}>
          <Card sx={{ textAlign: 'center', border: '2px solid #F57C00' }}>
            <CardContent>
              <Typography variant="h3" sx={{ color: '#F57C00', fontWeight: 'bold' }}>
                24/7
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Support Available
              </Typography>
              <Typography variant="caption" display="block" sx={{ mt: 1 }}>
                Technical assistance
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Key Benefits */}
      <Card sx={{ mb: 4, bgcolor: '#e3f2fd', border: '2px solid #1976D2' }}>
        <CardContent>
          <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold', color: '#1976D2' }}>
            Strategic Impact
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <Box component="ul" sx={{ pl: 2, '& li': { mb: 1 } }}>
                <li><Typography variant="body2"><strong>Global Security:</strong> Multilayered international defense network</Typography></li>
                <li><Typography variant="body2"><strong>Capacity Building:</strong> Strengthen partner customs capabilities</Typography></li>
                <li><Typography variant="body2"><strong>Standards Alignment:</strong> Common targeting methodologies worldwide</Typography></li>
                <li><Typography variant="body2"><strong>Technology Leadership:</strong> U.S. as global security innovator</Typography></li>
              </Box>
            </Grid>
            <Grid item xs={12} md={6}>
              <Box component="ul" sx={{ pl: 2, '& li': { mb: 1 } }}>
                <li><Typography variant="body2"><strong>Supply Chain Protection:</strong> Secure global trade networks</Typography></li>
                <li><Typography variant="body2"><strong>Threat Prevention:</strong> Identify risks at origin points</Typography></li>
                <li><Typography variant="body2"><strong>International Cooperation:</strong> Foster customs partnerships</Typography></li>
                <li><Typography variant="body2"><strong>Trade Facilitation:</strong> Expedite legitimate commerce globally</Typography></li>
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Training Programs */}
      <Card sx={{ mb: 4, bgcolor: '#f0f4f8' }}>
        <CardContent>
          <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold' }}>
            Training & Support Programs
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} md={4}>
              <Chip label="Basic Targeting Course" color="primary" sx={{ mb: 1, width: '100%' }} />
              <Typography variant="caption" display="block">2-week fundamentals training</Typography>
            </Grid>
            <Grid item xs={12} md={4}>
              <Chip label="Advanced Analytics" color="primary" sx={{ mb: 1, width: '100%' }} />
              <Typography variant="caption" display="block">Data analysis techniques</Typography>
            </Grid>
            <Grid item xs={12} md={4}>
              <Chip label="System Administration" color="primary" sx={{ mb: 1, width: '100%' }} />
              <Typography variant="caption" display="block">Platform management training</Typography>
            </Grid>
            <Grid item xs={12} md={4}>
              <Chip label="Intelligence Analysis" color="primary" sx={{ mb: 1, width: '100%' }} />
              <Typography variant="caption" display="block">Threat assessment methods</Typography>
            </Grid>
            <Grid item xs={12} md={4}>
              <Chip label="Train-the-Trainer" color="primary" sx={{ mb: 1, width: '100%' }} />
              <Typography variant="caption" display="block">Instructor development</Typography>
            </Grid>
            <Grid item xs={12} md={4}>
              <Chip label="Technical Support" color="primary" sx={{ mb: 1, width: '100%' }} />
              <Typography variant="caption" display="block">24/7 helpdesk access</Typography>
            </Grid>
          </Grid>
        </CardContent>
      </Card>
    </Container>
  );
};

export default ATSGlobalSystemDetail;
